import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";

// calculate counters from local data for real-time updates
const calculateCountersFromData = (requestsData) => {
  const total = requestsData.length;

  // pending: active=1 and not allocated
  const pending = requestsData.filter(
    (req) => req.active === 1 && !req.date_allocated
  ).length;

  // allocated: has date_allocated
  const allocated = requestsData.filter((req) => req.date_allocated).length;

  return {
    requests: total,
    pending,
    allocated,
  };
};

// get protection settings from config
const CACHING_ENABLED = config.enableCaching || false;
const CACHE_DURATION = config.protection?.cacheDuration || 5 * 60 * 1000;
const MIN_REFRESH_INTERVAL = config.protection?.minRefreshInterval || 3000;

// custom hook for managing requests data and API operations
export const useRequestsData = (showAllRequests = false) => {
  const [loading, setLoading] = useState(true);
  const [counters, setCounters] = useState({
    requests: 0,
    pending: 0,
    allocated: 0,
  });
  const [requestsData, setRequestsData] = useState([]);
  const [mostRecentRequest, setMostRecentRequest] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // rate limiting refs
  const lastFetchTime = useRef(0);
  const lastRefreshTime = useRef(0);
  const cachedData = useRef(null);
  const isFetching = useRef(false);

  // fetch requests from API
  const fetchRequests = useCallback(
    async (forceRefresh = false) => {
      const now = Date.now();

      // prevent concurrent fetches
      if (isFetching.current) {
        return;
      }

      // use cached data (only if caching enabled)
      if (
        CACHING_ENABLED &&
        !forceRefresh &&
        cachedData.current &&
        now - lastFetchTime.current < CACHE_DURATION
      ) {
        console.log(
          "using cached requests data (age: " +
            Math.round((now - lastFetchTime.current) / 1000) +
            "s)"
        );
        setRequestsData(cachedData.current);
        const calculatedCounters = calculateCountersFromData(
          cachedData.current
        );
        setCounters(calculatedCounters);
        setErrorMessage("");
        return;
      }

      isFetching.current = true;

      try {
        console.log(
          CACHING_ENABLED
            ? "fetching fresh requests data from API (caching enabled)..."
            : "fetching requests data from API (real-time mode)..."
        );

        const url = `${config.apiUrl}/allocate/requests${
          showAllRequests ? "?show_all=true" : ""
        }`;

        console.log(
          `fetching requests with showAllRequests=${showAllRequests}`
        );

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          // extract requests array and most_recent from response
          const requests = data.requests || data; // fallback if old format
          const mostRecent = data.most_recent || null;

          cachedData.current = requests;
          lastFetchTime.current = Date.now();

          setRequestsData(requests);
          setMostRecentRequest(mostRecent);

          // calculate counters from fresh data
          const calculatedCounters = calculateCountersFromData(requests);
          setCounters(calculatedCounters);
          setErrorMessage("");
          console.log(
            "fresh requests data fetched" +
              (CACHING_ENABLED ? " and cached" : "")
          );
        } else {
          console.error("failed to fetch requests:", data.message);
          setErrorMessage(data.message || "failed to load requests.");

          if (response.status === 401) {
            console.error("unauthorized - token may be expired");
          } else if (response.status >= 500) {
            setErrorMessage("server error. please try again later.");
          }
        }
      } catch (error) {
        console.error("network error fetching requests:", error);
        setErrorMessage(
          "unable to connect to the server. please check your connection."
        );
      } finally {
        isFetching.current = false;
      }
    },
    [showAllRequests]
  );

  // load data on mount
  useEffect(() => {
    const loadData = async () => {
      await fetchRequests(false);
      setLoading(false);
    };
    loadData();
  }, [fetchRequests]);

  // refresh data from API with rate limiting
  const performRefresh = useCallback(async () => {
    const now = Date.now();

    // rate limit refreshes
    if (now - lastRefreshTime.current < MIN_REFRESH_INTERVAL) {
      const waitTime = Math.ceil(
        (MIN_REFRESH_INTERVAL - (now - lastRefreshTime.current)) / 1000
      );
      console.warn(
        `refresh blocked: please wait ${waitTime}s before refreshing again`
      );
      setErrorMessage(
        `please wait ${waitTime} second${
          waitTime > 1 ? "s" : ""
        } before refreshing again.`
      );
      return false;
    }

    lastRefreshTime.current = now;
    setErrorMessage("");

    await fetchRequests(true);
    return true;
  }, [fetchRequests]);

  // allocate material to request with client-side validation
  const handleAllocate = useCallback(
    async (requestId, quantity) => {
      // Basic client-side validation
      if (!quantity || quantity <= 0) {
        return {
          success: false,
          message: "Please enter a valid quantity",
        };
      }

      // Find the request to validate bounds
      const request = requestsData.find((r) => r.id === requestId);
      if (request) {
        if (quantity < request.min_qty) {
          return {
            success: false,
            message: `Quantity must be at least ${request.min_qty} lbs (receiver's minimum)`,
          };
        }
        if (quantity > request.max_qty) {
          return {
            success: false,
            message: `Quantity cannot exceed ${request.max_qty} lbs (receiver's maximum)`,
          };
        }
        if (quantity > request.remaining_qty) {
          return {
            success: false,
            message: `Cannot allocate ${quantity} lbs. Only ${request.remaining_qty} lbs remaining.`,
          };
        }
        if (request.date_allocated) {
          return {
            success: false,
            message: "This request has already been allocated",
          };
        }
        if (!request.active) {
          return {
            success: false,
            message: "This request has been declined and cannot be allocated",
          };
        }
      }

      try {
        const url = `${config.apiUrl}/allocate/requests/${requestId}/allocate${
          showAllRequests ? "?show_all=true" : ""
        }`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({ quantity }),
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok && data.success) {
          await performRefresh();
          return {
            success: true,
            message: data.message || "material allocated successfully",
          };
        } else {
          return {
            success: false,
            message: data.message || "failed to allocate material",
          };
        }
      } catch (error) {
        console.error("error allocating material:", error);
        return {
          success: false,
          message: "unable to connect to the server",
        };
      }
    },
    [showAllRequests, performRefresh, requestsData]
  );

  // request changes to allocation
  const handleRequestChange = useCallback(
    async (requestId, changes) => {
      try {
        const url = `${
          config.apiUrl
        }/allocate/requests/${requestId}/request-change${
          showAllRequests ? "?show_all=true" : ""
        }`;

        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(changes),
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          await performRefresh();
          return {
            success: true,
            message: data.message || "changes requested successfully",
          };
        } else {
          return {
            success: false,
            message: data.message || "failed to submit changes",
          };
        }
      } catch (error) {
        console.error("error requesting changes:", error);
        return {
          success: false,
          message: "unable to connect to the server",
        };
      }
    },
    [showAllRequests, performRefresh]
  );

  // decline request using new validated endpoint
  const handleDecline = useCallback(
    async (requestId, reason = null) => {
      try {
        const url = `${config.apiUrl}/allocate/requests/${requestId}/decline${
          showAllRequests ? "?show_all=true" : ""
        }`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({ reason }),
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok && data.success) {
          await performRefresh();
          return {
            success: true,
            message: data.message || "request declined successfully",
          };
        } else {
          return {
            success: false,
            message: data.message || "failed to decline request",
          };
        }
      } catch (error) {
        console.error("error declining request:", error);
        return {
          success: false,
          message: "unable to connect to the server",
        };
      }
    },
    [showAllRequests, performRefresh]
  );

  return {
    loading,
    counters,
    requestsData,
    mostRecentRequest,
    errorMessage,
    setCounters,
    setRequestsData,
    setLoading,
    setErrorMessage,
    performRefresh,
    handleAllocate,
    handleRequestChange,
    handleDecline,
  };
};